/**
 * The Support at home landing once a plan is chosen — "Colin and Shelly's support at home plan"
 * (rebuild M2, 19 Oct 2026; the agreed mock-up's hub screen, j1-11). Aged care's AgedCareHub shape.
 *
 * One card for the chosen plan: its name in words, the big number against the support alone, the support
 * (each person's classification and services, their contribution) beside the money (Age Pension, each
 * year, the rate for each hour), then Explain this / What could go wrong / All the plans we tested.
 * Then the advice document and the next step, then a row of links. A breadcrumb so it is not taken
 * for Home.
 *
 * Since 22 Oct 2026 (owner: "Yes i want this all consistent") the card, the document and next step, "Model
 * something else" and the links are the shared pieces in components/strategy/ChosenPlanPage.tsx, so this
 * page and the other modules' plan pages cannot drift apart.
 *
 * Nothing here is computed except differences and the service totals: every money figure is the
 * chosen card's own (sahChosenPlan.ts), quoted with the doing-nothing figures it was measured against.
 */
import React, { useState } from 'react';
import { sahChosenOutOfDate } from './sahChosenStale.ts';
import { BookOpen, Hammer, ArrowLeftRight } from 'lucide-react';
import { ChosenPlanCard, DocumentAndNextStep, ModelSomethingElse, PlanPageLinks } from '../../components/strategy/ChosenPlanPage.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { ACAT_LEVEL_DATA, budgetForLevel } from '../../../../shared/src/calculation/constants/acatLevelData.ts';
import { computeServiceCostsForPerson } from './serviceBuilderCosts.ts';
import { SUPPORT_AT_HOME_SERVICES } from './supportAtHomeServices.tsx';
import { WORKFLOW_CONFIGS } from '../../pages/Dashboard/dashboardConfig.tsx';
import PlanExplanationModal from '../agedCare/PlanExplanationModal.tsx';
import StrategyRisksPanel from '../../components/strategy/StrategyRisksPanel.tsx';
import { buildStrategyRisks } from '../../strategy/strategyRisks.ts';
import { SahCauseEffect } from './SahPlanDialogs.tsx';
import { sahLeverKinds, type SahConfigLike } from './sahPlanWords.ts';
import type { ChosenSahPlan } from './sahChosenPlan.ts';
import { sahPricedServicePlan } from './sahEngineContext.ts';
import { SahLinkedCareSummary, linkedCareStory } from './SahLinkedCareSummary.tsx';
import { useSahLinkedCare } from './useSahLinkedPosition.ts';
import type { SavedStrategy } from '../../strategy/StrategyIntegrationUtils.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import DebtsInPlanNote from '../../components/strategy/DebtsInPlanNote.tsx';
import { sahDebtClearedBy } from './sahDebts.ts';
import { SAH_BENCH } from '../../utils/benchmarkWords.ts';

/** 'fresh' is a new model from scratch: step 1 with no services, the setup questions asked again (owner, 22 Oct 2026). */
export type SahHubIntent = 'services' | 'plans' | 'build' | 'build-new' | 'document' | 'guide' | 'assumptions' | 'yearByYear' | 'fresh';

export interface SahHubProps {
    factFind: DraftFactFind;
    plan: ChosenSahPlan;
    /** The service plan in the Service Builder now — to say when it differs from the one priced. */
    currentServicePlan: { client?: Record<string, unknown>; partner?: Record<string, unknown> } | null;
    supportStartDate?: string;
    /** The file's saved plans: the partner's aged care plan, when one is moving into care. */
    savedStrategies?: ReadonlyArray<SavedStrategy>;
    onOpen: (intent: SahHubIntent) => void;
    goHome: () => void;
}

const dateWords = (iso: string | undefined | null) => (iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : null);
const pct = (r: number) => `${+(r * 100).toFixed(1)}%`;
const money = (n: number) => formatCurrency(Math.abs(Math.round(n)));
const countWords = ['none', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

const Row = ({ label, value, sub }: { label: React.ReactNode; value: React.ReactNode; sub?: React.ReactNode }) => (
    <div className="flex items-baseline justify-between gap-4 border-b border-slate-100 py-2 text-[14px] last:border-b-0">
        <span className="text-slate-700">{label}</span>
        <span className="text-right tabular-nums"><span className="font-semibold text-slate-900">{value}</span>{sub ? <span className="ml-1.5 text-[12.5px] text-slate-500">{sub}</span> : null}</span>
    </div>
);

/** Each person's classification, budget and the services priced for them. */
function supportRows(factFind: DraftFactFind, plan: SahHubProps['currentServicePlan']) {
    const people: Array<{ key: 'client' | 'partner'; name: string }> = [{ key: 'client', name: String(factFind?.client?.firstName || 'You') }];
    if (factFind?.adviceFor === 'couple') people.push({ key: 'partner', name: String(factFind?.partner?.firstName || 'Partner') });
    return people.map(({ key, name }) => {
        const acat = (factFind?.careNeeds as Record<string, { status?: string; homeCareLevel?: string } | undefined> | undefined)?.[`${key}Acat`];
        const level = acat?.status === 'yes' ? acat.homeCareLevel : undefined;
        const label = level ? String(ACAT_LEVEL_DATA[level]?.label || '') : '';
        const classification = label.match(/classification\s*\d+/i)?.[0]?.toLowerCase() || (label ? label.replace(/^Support at Home\s*-\s*/i, '') : 'not assessed');
        const side = (plan?.[key] || {}) as Record<string, { quantity?: number | string; frequency?: string } | undefined>;
        const count = Object.values(side).filter((d) => d && Number(d.quantity) > 0).length;
        const value = computeServiceCostsForPerson(side as never, {}, SUPPORT_AT_HOME_SERVICES).total;
        const budget = level ? budgetForLevel(level) : 0;
        return { name, classification, count, value, budget, over: budget > 0 && value > budget ? value - budget : 0 };
    });
}

const SahHub = ({ factFind, plan, currentServicePlan, supportStartDate, savedStrategies = [], onOpen, goHome }: SahHubProps) => {
    const v = householdVoice(factFind);
    // With a partner moving into care, what their aged care plan did, as step 2 says it (24 Oct 2026).
    const linkedCare = useSahLinkedCare(factFind, savedStrategies);
    const card = plan.inputs!.card!;
    const f = card.plan;
    const b = card.baseline;
    const years = card.horizonYears || Number(plan.inputs?.legacyYears) || 10;
    const legacyDelta = b ? f.legacy - b.legacy : null;
    const pensionDelta = b ? f.pension - b.pension : null;
    const priced = (plan.inputs?.servicePlan || null) as SahHubProps['currentServicePlan'];
    const rows = supportRows(factFind, priced);
    // Compared as priced (a person moving into care has no services priced — sahEngineContext.ts).
    const servicesChanged = priced != null && currentServicePlan != null
        && JSON.stringify(sahPricedServicePlan(currentServicePlan, factFind)) !== String(plan.inputs?.servicePlanFingerprint ?? JSON.stringify(priced));
    // Chosen on another household (or lost on a re-test): the figures below are the ones it was chosen on (25 Oct 2026).
    const outOfDate = sahChosenOutOfDate(plan, factFind, savedStrategies);
    const assessed = rows.some((r) => r.classification !== 'not assessed');
    const nextStep = WORKFLOW_CONFIGS.home_care.steps[assessed ? 1 : 0];
    const start = supportStartDate ? dateWords(`${supportStartDate}T00:00:00`) : null;
    const [explainOpen, setExplainOpen] = useState(false);
    const [risksOpen, setRisksOpen] = useState(false);
    const doSentence = `${card.words.title}${card.words.sub ? ` — ${card.words.sub.replace(/^Paid from /, 'paid from ').replace(/\.$/, '')}` : ''}.`;
    const servicesLine = rows.map((r) => `${r.name}'s ${r.count > 0 ? `${countWords[r.count] ?? r.count} ${r.count === 1 ? 'service' : 'services'}` : 'none yet'}`).join(', ');

    const pensionNote = pensionDelta != null && Math.abs(pensionDelta) >= 1
        ? <span className={pensionDelta > 0 ? 'text-emerald-700' : 'text-rose-700'}>{pensionDelta > 0 ? '+' : '−'}{money(pensionDelta)} vs the support alone</span>
        : b ? 'same as the support alone' : undefined;
    const contributionNote = b
        ? (Math.abs(b.contribution - f.contribution) >= 1 ? `${money(b.contribution)} with the support alone` : 'same as the support alone')
        : undefined;
    const cashflowNote = b ? (b.cashflow < 0 ? `short ${money(b.cashflow)} with the support alone` : `${money(b.cashflow)} to spare with the support alone`) : undefined;

    return (
        <div className="mx-auto max-w-5xl" data-testid="sah-hub">
            <p className="text-[13px] text-slate-500"><button type="button" onClick={goHome} className="underline decoration-dotted underline-offset-2 hover:text-slate-800">Home</button> › Support at home</p>
            <h1 className="mt-2 text-[32px] font-bold leading-tight tracking-tight text-brand-navy">{v.Possessive} support at home plan</h1>
            <p className="mt-1 text-[15px] text-slate-600">
                Support starting {start ?? 'now'}; {servicesLine}.{' '}
                <button type="button" onClick={() => onOpen('services')} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy">Change the services</button>
            </p>

            {/* THE CHOSEN PLAN — the shared card every module uses (owner, 22 Oct 2026: "Yes i want this all consistent"). */}
            {/* Above the figures, so stale figures are never read as current; one warning at a time. */}
            {outOfDate ? (
                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] text-slate-700" data-testid="sah-hub-out-of-date">
                    <p><strong className="text-slate-900">{outOfDate.replace(/ — open it to choose again\.$/, '.')}</strong> The figures below are the ones it was chosen on{plan.createdAt ? `, ${dateWords(plan.createdAt)}` : ''}.</p>
                    <button type="button" onClick={() => onOpen('plans')} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-800 hover:border-blue-300">Choose a plan again</button>
                </div>
            ) : servicesChanged && (
                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] text-slate-700" data-testid="sah-hub-services-changed">
                    <p><strong className="text-slate-900">The services have changed since this plan was chosen.</strong> The figures below are for the services it was priced on.</p>
                    <button type="button" onClick={() => onOpen('plans')} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-800 hover:border-blue-300">Test the plans again</button>
                </div>
            )}

            <div className="mt-5">
                <ChosenPlanCard
                    testIdPrefix="sah-hub"
                    chosenOn={dateWords(plan.createdAt)}
                    builtByYou={card.origin === 'manual'}
                    title={card.words.title}
                    sub={card.words.sub || undefined}
                    estate={f.legacy}
                    years={years}
                    estateDelta={legacyDelta}
                    baseWord={SAH_BENCH.word}
                    figures={[
                        { label: `${v.Possessive} contribution`, value: money(f.contribution), unit: 'a year', note: contributionNote, testId: 'sah-hub-contribution' },
                        { label: 'Age Pension', value: money(f.pension), unit: 'a year', note: pensionNote, testId: 'sah-hub-pension' },
                        { label: f.cashflow < 0 ? 'Each year, short by' : 'Left over each year', value: money(f.cashflow), note: cashflowNote, tone: f.cashflow >= 0 ? 'good' : 'plain', testId: 'sah-hub-cashflow' },
                    ]}
                    onExplain={() => setExplainOpen(true)}
                    onRisks={() => setRisksOpen(true)}
                    onAllPlans={() => onOpen('plans')}
                >
                    <h3 className="text-[15px] font-bold text-brand-navy">The support</h3>
                    <div className="mt-1.5" data-testid="sah-hub-support">
                        {rows.map((r) => (
                            <Row key={r.name} label={`${r.name} · ${r.classification}`} value={r.count > 0 ? `${money(r.value)} of services a year` : <span className="font-normal text-slate-500">none yet</span>} sub={r.budget > 0 ? `budget ${money(r.budget)}` : undefined} />
                        ))}
                        {f.rates && (
                            <Row
                                label="Contribution for each hour"
                                value={`${pct(f.rates.independence)} and ${pct(f.rates.everydayLiving)}`}
                                sub={b?.rates && (Math.abs(b.rates.independence - f.rates.independence) > 0.0005 || Math.abs(b.rates.everydayLiving - f.rates.everydayLiving) > 0.0005) ? `was ${pct(b.rates.independence)} and ${pct(b.rates.everydayLiving)}` : undefined}
                            />
                        )}
                    </div>
                    {rows.filter((r) => r.over > 0).map((r) => (
                        <p key={r.name} className="mt-2 text-[13px] text-slate-600" data-testid="sah-hub-over-budget">
                            {r.name}&apos;s services are {money(r.over)} more than the budget; that part is paid at full price.{' '}
                            <button type="button" onClick={() => onOpen('services')} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4">Review the services</button>
                        </p>
                    ))}
                    {/* No "When the money runs out" here: the saved card keeps no projection rows or runway
                        (sahCardRecord drops them, the 413 on save), so the year-by-year figures under Explain this carry it. */}
                </ChosenPlanCard>
            </div>

            {linkedCare && (
                <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-card" data-testid="sah-hub-linked">
                    <p className="mb-2 text-[15px] font-bold text-brand-navy">With {linkedCareStory(linkedCare).enteringName} moving into aged care</p>
                    <SahLinkedCareSummary linkedCare={linkedCare} />
                </div>
            )}

            {/* THE DOCUMENT AND THE NEXT STEP */}
            <div className="mt-4">
                <DocumentAndNextStep
                    documentState={plan.soaSnapshot ? 'prepared' : 'not prepared yet'}
                    documentLine="The written record of this plan: the services it was priced on, what it assumes, what it costs, what could go wrong."
                    documentAction={<button type="button" onClick={() => onOpen('document')} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900" data-testid="sah-hub-document">{plan.soaSnapshot ? 'Open the advice document' : 'Get the advice document'}</button>}
                    nextTitle={(nextStep?.name ?? 'Contribution assessment').toLowerCase()}
                    nextLine={nextStep?.description}
                    nextAction={<button type="button" onClick={goHome} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900" data-testid="sah-hub-home">Full action plan on Home</button>}
                />
            </div>

            {/* MODEL SOMETHING ELSE — support at home keeps one plan per file, so the two ways on are new services
                with the same answers, or every question asked afresh (owner, 22 Oct 2026). */}
            <div className="mt-4">
                <ModelSomethingElse
                    testId="sah-hub-model-something-else"
                    options={[
                        { title: 'Change the services, same answers', body: `Change ${v.their} services on step 1 and test the plans again, keeping ${v.their} answers to the setup questions.`, action: 'Change the services', onClick: () => onOpen('services'), testId: 'sah-hub-change-services' },
                        { title: 'A new model from scratch', body: 'Start again on step 1 with no services, and answer the setup questions afresh.', action: 'Start a new model from scratch', onClick: () => onOpen('fresh'), testId: 'sah-hub-start-fresh' },
                    ]}
                />
            </div>

            {/* THE WAYS ON — links, none of them the next step. */}
            <div className="mt-5" data-testid="sah-hub-links">
                <PlanPageLinks
                    links={[
                        { label: 'All the plans we tested', icon: <ArrowLeftRight size={14} />, onClick: () => onOpen('plans') },
                        // Two doors (owner, 25 Oct 2026): change this plan, or start a blank one.
                        { label: card.origin === 'manual' ? 'Change the plan you built' : 'Customise this plan', icon: <Hammer size={14} />, onClick: () => onOpen('build'), testId: 'sah-hub-build' },
                        { label: 'Build a plan yourself', icon: <Hammer size={14} />, onClick: () => onOpen('build-new'), testId: 'sah-hub-build-new' },
                        { label: 'How Support at home works — the guide', icon: <BookOpen size={14} />, onClick: () => onOpen('guide') },
                        { label: 'Rates and assumptions', icon: <BookOpen size={14} />, onClick: () => onOpen('assumptions') },
                    ]}
                />
            </div>

            {explainOpen && (
                <PlanExplanationModal
                    eyebrow="Explain this"
                    title={card.words.title}
                    intro={`Each line says what ${v.they} do, and what that does to the numbers. ${SAH_BENCH.sentence}`}
                    onClose={() => setExplainOpen(false)}
                    // The year-by-year figures, as on every other Explain this (owner, 19 Oct 2026: missing here).
                    // The saved card keeps no projection rows, so the plans step opens them for this plan.
                    footer={<button type="button" onClick={() => { setExplainOpen(false); onOpen('yearByYear'); }} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-[14px] font-semibold text-slate-800 hover:border-blue-300 hover:bg-blue-50" data-testid="sah-hub-year-by-year">See the year-by-year figures</button>}
                >
                    <SahCauseEffect doSentence={doSentence} treatment={card.words.treatment} plan={f} baseline={b} horizonYears={years} possessive={v.their} they={v.they} drawLines={card.drawLines} leverKinds={card.engineConfig ? sahLeverKinds(card.engineConfig as SahConfigLike) : card.riskKinds} />
                    <DebtsInPlanNote factFind={factFind} clearedAmount={sahDebtClearedBy(card.engineConfig as never)} years={years} context={card.origin === 'manual' ? 'manual' : 'chosen'} />
                </PlanExplanationModal>
            )}
            {risksOpen && (
                <StrategyRisksPanel strategyName={card.words.title} voice={v} onClose={() => setRisksOpen(false)} risks={buildStrategyRisks(card.riskKinds)} />
            )}
        </div>
    );
};

export default SahHub;
